<script lang="ts" setup>
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Title } from '@/components/ui/title'
import { formatCpf } from '@/helpers/document'
import { formatText } from '@/helpers/format-text'
import { FormSchema } from '@/pages/carts/show.vue'
import { ref } from 'vue'
import BrazillianAddress from './BrazillianAddress.vue'
import ForeignAddress from './ForeignAddress.vue'

const { isLoading, form } = defineProps<{
    form: FormSchema
    isLoading: boolean
}>()

const document = ref('brazilian')

const emit = defineEmits(['searchAddressByZipCode'])
</script>

<template>
    <section class="mt-8 space-y-8">
        <fieldset>
            <Title as="legend" size="sm">{{ $t('Personal data') }}</Title>
            <p class="mb-8">{{ $t('Enter your personal data') }}</p>
            <div class="grid gap-x-2 gap-y-6 md:grid-cols-2 lg:gap-x-8">
                <div class="flex flex-col gap-2">
                    <Label required for="name">{{ $t('Name') }} </Label>
                    <Input
                        id="name"
                        type="text"
                        :placeholder="$t('Enter your name')"
                        v-model="form.full_name"
                        @input="(e: Event) => (form.full_name = formatText((e.target as HTMLInputElement).value))"
                    />
                    <p v-if="form.errors.full_name" class="text-destructive mt-px text-xs">{{ $t(form.errors.full_name) }}</p>
                </div>
                <div class="flex flex-col gap-2">
                    <div v-if="form.has_foreign_doc">
                        <Label required for="cpf">{{ $t('Foreign document') }} </Label>
                        <Input id="cpf" type="text" placeholder="000000000" v-model="form.ssn" />
                    </div>

                    <div v-else>
                        <Label required for="cpf">{{ $t('CPF') }} </Label>
                        <Input
                            id="cpf"
                            type="text"
                            placeholder="000.000.000-00"
                            v-model="form.ssn"
                            v-if="document === 'brazilian'"
                            @input="(e: Event) => (form.ssn = formatCpf((e.target as HTMLInputElement).value))"
                        />
                    </div>

                    <p v-if="form.errors.ssn" class="text-destructive mt-px text-xs">{{ $t(form.errors.ssn) }}</p>
                    <div class="flex items-center gap-2">
                        <Checkbox
                            id="foreign-document"
                            v-model="form.has_foreign_doc"
                            v-on:update:model-value="(check) => (check === true ? (document = 'foreign-document') : (document = 'brazilian'))"
                        />
                        <Label class="text-xs text-slate-500" for="foreign-document">{{ $t('I do not have a document issued in Brazil') }}</Label>
                    </div>
                </div>
                <div class="flex flex-col gap-2">
                    <Label required for="email">{{ $t('Email to send the invoice') }} </Label>
                    <Input id="email" type="text" placeholder="email@email.com" v-model="form.email" />
                    <p v-if="form.errors.email" class="text-destructive mt-px text-xs">{{ $t(form.errors.email) }}</p>
                </div>

                <div class="flex flex-col gap-2">
                    <Label required for="phone">{{ $t('Cellphone') }} </Label>

                    <Input id="phone" type="tel" :placeholder="$t('(00) 00000-0000')" v-model="form.phone_number" />
                    <p v-if="form.errors.phone_number" class="text-destructive mt-px text-xs">{{ $t(form.errors.phone_number) }}</p>
                </div>
                <div class="flex flex-col gap-2">
                    <Label for="phone">{{ $t('Phone') }} </Label>

                    <Input id="phone" type="tel" :placeholder="$t('(00) 0000-0000')" v-model="form.another_phone_number" />
                    <p v-if="form.errors.another_phone_number" class="text-destructive mt-px text-xs">{{ $t(form.errors.another_phone_number) }}</p>
                </div>
            </div>
        </fieldset>

        <fieldset>
            <Title as="legend" size="sm" class="mb-8">{{ $t('Address') }}</Title>

            <ForeignAddress v-if="form.has_foreign_doc" :form="form" @searchAddressByZipCode="emit('searchAddressByZipCode')" />

            <BrazillianAddress v-else :form="form" :isLoading="isLoading" @searchAddressByZipCode="emit('searchAddressByZipCode')" />
        </fieldset>
    </section>
</template>
